import * as DialogPrimitive from "@radix-ui/react-dialog";
import clsx from "clsx";
import React from "react";
import { Button } from "../button/Button";
import { IconButton } from "../iconButton/IconButton";
import * as styles from "./Dialog.css";

export type DialogSize = "sm" | "md" | "lg" | "full";

export interface DialogProps {
  /**
   * Dialog content
   */
  children?: React.ReactNode;
  /**
   * Dialog title
   */
  title?: string;
  /**
   * Dialog description
   */
  description?: string;

  /**
   * Confirm button text
   */
  confirmText?: string;
  /**
   * Cancel button text
   */
  cancelText?: string;
  /**
   * Dialog size
   */
  size?: DialogSize;
  /**
   * Hide close icon
   */
  hideCloseIcon?: boolean;
  /**
   * If true, renders the dialog as a modal with an overlay.
   * If false, displays the dialog inline next to its trigger without an overlay.
   */
  modal?: boolean;
  /**
   * Controlled open state
   * */
  open?: boolean;
  /**
   * Confirm handler
   */
  onConfirm?: () => void | boolean | Promise<void | boolean>;
  /**
   * Open state change handler
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Close handler
   */
  onClose?: () => void;
}

export function Dialog({
  title,
  description,
  children,
  confirmText,
  cancelText,
  size = "md",
  hideCloseIcon,
  modal = true,
  open,
  onConfirm,
  onOpenChange,
  onClose
}: DialogProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange} modal={modal}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className={styles.overlay} />
        <DialogPrimitive.Content
          {...(!description && { "aria-describedby": undefined })}
          className={clsx(
            modal && styles.modalOverlay,
            styles.baseContent,
            styles.content[size]
          )}
        >
          {/* Header */}

          <header className={styles.header}>
            <div className={styles.titleRow}>
              <DialogPrimitive.Title className={styles.title}>
                {title ?? ""}
              </DialogPrimitive.Title>

              {!hideCloseIcon && (
                <DialogPrimitive.Close asChild>
                  <IconButton
                    iconName="close"
                    variant="secondary"
                    size="md"
                    aria-label="Close"
                    onClick={onClose}
                  />
                </DialogPrimitive.Close>
              )}
            </div>
            {description && (
              <DialogPrimitive.Description className={styles.description}>
                {description}
              </DialogPrimitive.Description>
            )}
          </header>

          {/* Body */}
          {children && <div className={styles.body}>{children}</div>}

          {/* Footer */}
          {(cancelText || confirmText) && (
            <footer className={styles.footer}>
              {cancelText && (
                <DialogPrimitive.Close asChild>
                  <Button type="button" variant="secondary" onClick={onClose}>
                    {cancelText}
                  </Button>
                </DialogPrimitive.Close>
              )}
              {confirmText && (
                <Button
                  onClick={async () => {
                    let result;
                    if (onConfirm) {
                      result = await onConfirm();
                    }
                    if (result !== false && onOpenChange) {
                      onOpenChange(false);
                    }
                  }}
                  variant="primary"
                >
                  {confirmText}
                </Button>
              )}
            </footer>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export default Dialog;
